import React, { useState, useMemo } from 'react';
import { History, Save, Users, Search } from "lucide-react";

const DEFAULT_ITEMS = ["LPG 20kg", "LPG 50kg", "산소(O2)", "질소(N2)", "아르곤(Ar)"];
const STATUSES = ['실병', '공병'];

// 실물 용기 개수로는 표현할 수 없는 음수분을 stockAdjustments에 별도 저장 (locType|location|item|status → 조정치)
const adjKey = (locType, location, item, status) => `${locType}|${location}|${item}|${status}`;

const InitialStock = ({ customers = [], cylinders = [], setCylinders, warehouses = [], items = [], stockAdjustments = {}, setStockAdjustments }) => {
  const itemTypes = useMemo(() => {
    const fromSettings = items.length > 0 ? items.map(i => i.name || i) : [];
    const fromCylinders = cylinders.map(c => c.item);
    return [...new Set([...DEFAULT_ITEMS, ...fromSettings, ...fromCylinders])];
  }, [cylinders, items]);

  const [warehouseInputs, setWarehouseInputs] = useState(() => {
    const inputs = {};
    warehouses.forEach(w => {
      inputs[w] = {};
      const fromSettings = items.length > 0 ? items.map(i => i.name || i) : [];
      const allItems = [...new Set([...DEFAULT_ITEMS, ...fromSettings, ...cylinders.map(c => c.item)])];
      allItems.forEach(item => {
        inputs[w][item] = {
          실병: cylinders.filter(c => c.locType === 'warehouse' && c.location === w && c.item === item && c.status === '실병').length
            + (stockAdjustments[adjKey('warehouse', w, item, '실병')] || 0),
          공병: cylinders.filter(c => c.locType === 'warehouse' && c.location === w && c.item === item && c.status === '공병').length
            + (stockAdjustments[adjKey('warehouse', w, item, '공병')] || 0),
        };
      });
    });
    return inputs;
  });

  const [customerInputs, setCustomerInputs] = useState(() => {
    const inputs = {};
    customers.forEach(cu => {
      inputs[cu.name] = {};
      const fromSettings = items.length > 0 ? items.map(i => i.name || i) : [];
      const allItems = [...new Set([...DEFAULT_ITEMS, ...fromSettings, ...cylinders.map(c => c.item)])];
      allItems.forEach(item => {
        inputs[cu.name][item] = {
          실병: cylinders.filter(c => c.locType === 'customer' && c.location === cu.name && c.item === item && c.status === '실병').length
            + (stockAdjustments[adjKey('customer', cu.name, item, '실병')] || 0),
          공병: cylinders.filter(c => c.locType === 'customer' && c.location === cu.name && c.item === item && c.status === '공병').length
            + (stockAdjustments[adjKey('customer', cu.name, item, '공병')] || 0),
        };
      });
    });
    return inputs;
  });

  const [saved, setSaved] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  const filteredCustomers = useMemo(() =>
    customerSearch.trim() === ''
      ? customers
      : customers.filter(cu => cu.name.toLowerCase().includes(customerSearch.trim().toLowerCase())),
    [customers, customerSearch]
  );

  const handleChange = (setter, locationKey, item, status, value) => {
    // "-"만 입력한 중간 상태를 그대로 두어 음수 입력이 가능하게 함
    const parsed = value === '' || value === '-' ? value : (parseInt(value, 10) || 0);
    setter(prev => ({
      ...prev,
      [locationKey]: {
        ...prev[locationKey],
        [item]: {
          ...(prev[locationKey]?.[item] || { 실병: 0, 공병: 0 }),
          [status]: parsed,
        },
      },
    }));
  };

  const handleSave = () => {
    if (!setCylinders) return;
    let next = [...cylinders];
    let idSeed = Date.now();
    const newAdjustments = { ...stockAdjustments };

    // 용기는 물리적으로 음수 개수를 가질 수 없으므로, 입력값 중 음수분은
    // stockAdjustments에 별도 저장하고 실물 용기는 0개까지만 맞춘다.
    const reconcile = (locType, locationKey, item, status, desiredRaw) => {
      const desiredTotal = parseInt(desiredRaw, 10) || 0;
      const objectTarget  = Math.max(desiredTotal, 0);
      const adjustment    = desiredTotal - objectTarget; // 0 또는 음수
      const key = adjKey(locType, locationKey, item, status);
      if (adjustment !== 0) newAdjustments[key] = adjustment;
      else delete newAdjustments[key];

      const current = next.filter(c =>
        c.locType === locType && c.location === locationKey && c.item === item && c.status === status
      );
      const diff = objectTarget - current.length;
      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          next.push({ id: `INIT-${idSeed++}`, item, status, locType, location: locationKey });
        }
      } else if (diff < 0) {
        let toRemove = -diff;
        next = next.filter(c => {
          if (toRemove > 0 && c.locType === locType && c.location === locationKey && c.item === item && c.status === status) {
            toRemove--;
            return false;
          }
          return true;
        });
      }
    };

    warehouses.forEach(w => {
      itemTypes.forEach(item => {
        STATUSES.forEach(status => {
          reconcile('warehouse', w, item, status, warehouseInputs[w]?.[item]?.[status] ?? 0);
        });
      });
    });
    customers.forEach(cu => {
      itemTypes.forEach(item => {
        STATUSES.forEach(status => {
          reconcile('customer', cu.name, item, status, customerInputs[cu.name]?.[item]?.[status] ?? 0);
        });
      });
    });

    setCylinders(next);
    if (setStockAdjustments) setStockAdjustments(newAdjustments);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 창고별 이월 재고 */}
      <div style={boxStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <History color="#185FA5" />
          <h3 style={{ margin: 0 }}>창고별 이월(기초) 재고 설정</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f0f4f8' }}>
                <th style={{ ...thStyle, borderBottom: 'none' }} rowSpan={2}>창고명</th>
                {itemTypes.map(item => (
                  <th key={item} style={{ ...thStyle, textAlign: 'center', borderBottom: 'none', borderLeft: '2px solid #e2e8f0' }} colSpan={2}>{item}</th>
                ))}
              </tr>
              <tr style={{ background: '#f8fafc' }}>
                {itemTypes.map(item => (
                  <React.Fragment key={item}>
                    <th style={{ ...subThStyle, borderLeft: '2px solid #e2e8f0', color: '#185FA5' }}>실병</th>
                    <th style={{ ...subThStyle, color: '#888' }}>공병</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {warehouses.length === 0
                ? <tr><td colSpan={itemTypes.length * 2 + 1} style={{ ...tdStyle, color: '#aaa', textAlign: 'center' }}>등록된 창고가 없습니다.</td></tr>
                : warehouses.map(w => (
                  <tr key={w}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{w}</td>
                    {itemTypes.map(item => (
                      <React.Fragment key={item}>
                        <td style={{ ...tdStyle, borderLeft: '2px solid #e2e8f0' }}>
                          <input style={realInput} type="number"
                            value={warehouseInputs[w]?.[item]?.실병 ?? 0}
                            onChange={e => handleChange(setWarehouseInputs, w, item, '실병', e.target.value)} />
                        </td>
                        <td style={tdStyle}>
                          <input style={emptyInput} type="number"
                            value={warehouseInputs[w]?.[item]?.공병 ?? 0}
                            onChange={e => handleChange(setWarehouseInputs, w, item, '공병', e.target.value)} />
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* 거래처별 이월 재고 */}
      <div style={boxStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Users color="#059669" />
          <h3 style={{ margin: 0 }}>거래처별 이월(미회수) 재고 설정</h3>
        </div>
        <div style={{ position: 'relative', marginBottom: 16, maxWidth: 300 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="거래처 검색..."
            value={customerSearch}
            onChange={e => setCustomerSearch(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f0f8f4' }}>
                <th style={{ ...thStyle, borderBottom: 'none' }} rowSpan={2}>거래처명</th>
                {itemTypes.map(item => (
                  <th key={item} style={{ ...thStyle, textAlign: 'center', borderBottom: 'none', borderLeft: '2px solid #e2e8f0' }} colSpan={2}>{item}</th>
                ))}
              </tr>
              <tr style={{ background: '#f8fafc' }}>
                {itemTypes.map(item => (
                  <React.Fragment key={item}>
                    <th style={{ ...subThStyle, borderLeft: '2px solid #e2e8f0', color: '#185FA5' }}>실병</th>
                    <th style={{ ...subThStyle, color: '#888' }}>공병</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.length === 0
                ? <tr><td colSpan={itemTypes.length * 2 + 1} style={{ ...tdStyle, color: '#aaa', textAlign: 'center' }}>등록된 거래처가 없습니다.</td></tr>
                : filteredCustomers.length === 0
                ? <tr><td colSpan={itemTypes.length * 2 + 1} style={{ ...tdStyle, color: '#aaa', textAlign: 'center' }}>검색 결과가 없습니다.</td></tr>
                : filteredCustomers.map(cu => (
                  <tr key={cu.id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{cu.name}</td>
                    {itemTypes.map(item => (
                      <React.Fragment key={item}>
                        <td style={{ ...tdStyle, borderLeft: '2px solid #e2e8f0' }}>
                          <input style={realInput} type="number"
                            value={customerInputs[cu.name]?.[item]?.실병 ?? 0}
                            onChange={e => handleChange(setCustomerInputs, cu.name, item, '실병', e.target.value)} />
                        </td>
                        <td style={tdStyle}>
                          <input style={emptyInput} type="number"
                            value={customerInputs[cu.name]?.[item]?.공병 ?? 0}
                            onChange={e => handleChange(setCustomerInputs, cu.name, item, '공병', e.target.value)} />
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      <button onClick={handleSave} style={{ ...saveBtn, background: saved ? '#059669' : '#333' }}>
        <Save size={18} />
        {saved ? '저장 완료!' : '기초 재고 확정저장'}
      </button>
    </div>
  );
};

const boxStyle        = { background: 'white', padding: 25, borderRadius: 16, border: '1px solid #d3d1c7' };
const tableStyle      = { width: '100%', borderCollapse: 'collapse' };
const thStyle         = { padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 700 };
const subThStyle      = { padding: '6px 8px', borderBottom: '2px solid #eee', textAlign: 'center', fontSize: 11, fontWeight: 600 };
const tdStyle         = { padding: '10px 8px', borderBottom: '1px solid #eee', fontSize: 14 };
const realInput       = { width: '56px', padding: '5px', border: '1px solid #93c5fd', textAlign: 'center', borderRadius: 4, background: '#eff6ff' };
const emptyInput      = { width: '56px', padding: '5px', border: '1px solid #ddd', textAlign: 'center', borderRadius: 4, background: '#f9f9f9', color: '#888' };
const saveBtn         = { padding: '15px', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'center', gap: 8, transition: 'background 0.2s' };
const searchInputStyle = { width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default InitialStock;
